import React, { useImperativeHandle, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useRecoilValue } from 'recoil';

import { TableContextProvider } from 'lib/context/TableContext/TableContextProvider';

import { currentPractice } from 'components/practice/practiceState';

import { upsertNonERXPrescription } from '../../../../../../../api/NonERXPrescription';
import Button from '../../../../../../shared/Buttons/Button';
import { permissions, userState } from '../../../../../../state';
import {
  MedicalPrescriptionlValidationSchema,
  getInitialMedicalPrescriptionData
} from '../../lib/handleFormik';

import Medications from './components/Medications/Medications';
import nonERXMedicationTableCols from './components/Medications/lib/nonERXMedicationTableCols';
import NonERXPrescriptionAndFrequency from './components/NonERXPrescriptionAndFrequency';
import PatientInfo from './components/PatientInfo';
import YourSignatureMedical from './components/YourSignatureMedical';
import { patientMapper } from './lib/patientMapper';

const MedicalPrescriptionCreate = ({ actionRef, itemProps = {}, setActiveView }) => {
  const { patient: outletPatient } = useOutletContext() || {};

  const [showMedicationModal, setShowMedicationModal] = useState({ open: false });
  const userPermissions = useRecoilValue(permissions);
  const practice = useRecoilValue(currentPractice);

  const user = useRecoilValue(userState);

  const { appointmentId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const upsertNonERXPrescriptionMutation = useMutation(
    (data) => upsertNonERXPrescription(navigate, data),
    {
      onSuccess: async (data) => {
        const { nonERXPrescription } = data?.data || {};
        await Promise.all([
          queryClient.invalidateQueries(['orders']),
          queryClient.invalidateQueries(['medication'])
        ]);
        formik.setValues(nonERXPrescription);
        setActiveView((prevState) => ({
          ...prevState,
          loading: false,
          component: 'preview',
          itemProps: nonERXPrescription
        }));
      },
      onSettled: () => {
        setActiveView((prevState) => ({ ...prevState, loading: false }));
      }
    }
  );

  const initialFormikValues = {
    provider_id: user?.id,
    appointment_id: appointmentId,
    ...patientMapper({ ...itemProps, patient: outletPatient || itemProps?.patient }),
    ...itemProps
  };

  const formik = useFormik({
    initialValues: getInitialMedicalPrescriptionData(initialFormikValues),
    validationSchema: MedicalPrescriptionlValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setActiveView((prevState) => ({ ...prevState, loading: true }));

      const signatureDataValues = structuredClone(values?.signatureData);
      const medications = {
        upsert: structuredClone(values?.medications)?.map((medication) => {
          delete medication?.tempId;
          return medication;
        }),
        delete: structuredClone(values?.deleteMedicationIds)
      };

      const data = structuredClone(values);
      delete data?.signatureData;
      delete data?.medications;
      delete data?.deleteMedicationIds;

      await upsertNonERXPrescriptionMutation.mutateAsync({
        upsertData: data,
        medications,
        signatureData: { ...signatureDataValues, link: pathname }
      });
    }
  });

  useImperativeHandle(actionRef, () => ({
    onSubmit: formik.submitForm
  }));

  return (
    <div>
      <div className="!mt-6 rounded-lg bg-primary-50 !p-3">
        <PatientInfo formik={formik} />
      </div>

      <div className="!mt-6 rounded-lg bg-primary-50 !p-3">
        <NonERXPrescriptionAndFrequency formik={formik} />
      </div>

      <div className="!mt-6 rounded-lg bg-primary-50 !p-3">
        <div className="flex w-full justify-end pb-3">
          <Button
            text="Add Medication"
            size="small"
            onClick={() => setShowMedicationModal({ open: true })}
          />
        </div>
        <TableContextProvider
          key={`non-erx-prescription-medications-${formik?.values?.medications?.length}`}
          cols={nonERXMedicationTableCols({
            showMedicationModal,
            setShowMedicationModal,
            userPermissions,
            practice,
            formik
          })}
          name="Medications"
          defaultFilters={[]}>
          <Medications
            {...{
              formik,
              showMedicationModal,
              setShowMedicationModal
            }}
          />
        </TableContextProvider>
      </div>

      <div className="!mt-6 rounded-lg bg-primary-50 !p-3">
        <YourSignatureMedical formik={formik} />
      </div>
    </div>
  );
};

export default MedicalPrescriptionCreate;
